import { useEffect, useState } from 'react';

/**
 * The split a list surface needs so its header survives a refetch.
 *
 * `showLoadingSkeleton` is the ONLY flag that should drive a full-surface
 * loading fallback (the skeleton that replaces header + list). It is true
 * solely while the FIRST fetch is in flight. Every later fetch -- the one a
 * search keystroke, a filter change, or a real-time push triggers -- reports
 * through `isRefreshing` instead, so the header (and its focused, locally
 * controlled search input) stays mounted and keeps the text the user typed.
 */
export interface FirstLoadGate {
  /** True only while the first fetch has not resolved. Drives the skeleton. */
  showLoadingSkeleton: boolean;
  /** True while a fetch after the first is in flight (header stays mounted). */
  isRefreshing: boolean;
}

/**
 * Splits a raw `isLoading` flag into "first load" vs "refreshing" using whether
 * the first fetch has already resolved.
 *
 * The clients list regressed because it drove its header's mount on the raw
 * `isLoading`: every debounced search keystroke refetched, `isLoading` flipped
 * true, the header unmounted, and the locally-held input text and focus were
 * destroyed -- the typed letter vanished. Gating the skeleton on "first load
 * only" keeps the header mounted on every refetch, so the input retains its
 * value and focus; the properties list already relied on this exact split.
 *
 * @param isLoading - Whether a fetch is currently in flight.
 * @param hasLoadedOnce - Whether the first fetch has already resolved.
 * @returns The skeleton/refresh split for the surface.
 */
export function computeFirstLoadGate(isLoading: boolean, hasLoadedOnce: boolean): FirstLoadGate {
  return {
    showLoadingSkeleton: isLoading && !hasLoadedOnce,
    isRefreshing: isLoading && hasLoadedOnce,
  };
}

/**
 * Tracks first-load completion and returns the skeleton/refresh split.
 *
 * Mirrors the properties list's inline `hasLoadedOnce` tracker, extracted so
 * both lists share one definition of "a refetch must not unmount the header".
 *
 * @param isLoading - Whether a fetch is currently in flight.
 * @returns The first-load gate for the surface.
 */
export function useFirstLoadGate(isLoading: boolean): FirstLoadGate {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!isLoading) setHasLoadedOnce(true);
  }, [isLoading]);
  return computeFirstLoadGate(isLoading, hasLoadedOnce);
}
